import { useState } from "react";
import { useLocation } from "wouter";
import { 
  Zap, 
  Search, 
  Copy, 
  ArrowRight,
  TrendingUp,
  FileText,
  Target,
  Shield,
  Lightbulb,
  Users,
  BarChart3,
  Globe
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const smartPrompts = [
  {
    id: 1,
    title: "Market Analysis",
    prompt: "Analyze the competitive landscape for AI-powered fintech startups in the UK market, focusing on regulatory challenges and growth opportunities.",
    category: "Market Research",
    icon: TrendingUp,
    color: "blue"
  },
  {
    id: 2,
    title: "Investment Thesis",
    prompt: "Create a comprehensive investment thesis for a SaaS startup targeting small businesses, including market size, competitive advantages, and financial projections.",
    category: "Investment",
    icon: FileText,
    color: "green"
  },
  {
    id: 3,
    title: "Product Strategy",
    prompt: "Develop a go-to-market strategy for a B2B AI platform, including target customer segments, pricing models, and distribution channels.",
    category: "Strategy",
    icon: Target,
    color: "purple"
  },
  {
    id: 4,
    title: "Risk Assessment",
    prompt: "Conduct a comprehensive risk analysis for entering the European renewable energy market, covering regulatory, financial, and operational risks.",
    category: "Risk Analysis",
    icon: Shield,
    color: "red"
  },
  {
    id: 5,
    title: "Technology Trends",
    prompt: "Analyze emerging technology trends in quantum computing and their potential impact on cybersecurity over the next 5 years.",
    category: "Technology",
    icon: Lightbulb,
    color: "yellow"
  },
  {
    id: 6,
    title: "Customer Research",
    prompt: "Design a customer research framework to understand pain points and preferences for enterprise software buyers in the healthcare sector.",
    category: "Research",
    icon: Users,
    color: "indigo"
  },
  {
    id: 7,
    title: "Business Model",
    prompt: "Evaluate different business models for a sustainability-focused marketplace platform, comparing subscription vs. commission-based approaches.",
    category: "Business Model",
    icon: BarChart3,
    color: "pink"
  },
  {
    id: 8,
    title: "Global Expansion",
    prompt: "Create an expansion plan for a UK-based fintech company looking to enter Asian markets, focusing on regulatory requirements and local partnerships.",
    category: "Expansion",
    icon: Globe,
    color: "cyan"
  }
];

export default function SmartPromptsPage() {
  const [, setLocation] = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const categories = ["all", ...Array.from(new Set(smartPrompts.map(p => p.category)))];

  const filteredPrompts = smartPrompts.filter(prompt => {
    const matchesSearch = prompt.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         prompt.prompt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         prompt.category.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = selectedCategory === "all" || prompt.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const getColorClasses = (color: string) => {
    const colors = {
      blue: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      green: "bg-green-500/20 text-green-400 border-green-500/30",
      purple: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      red: "bg-red-500/20 text-red-400 border-red-500/30",
      yellow: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      indigo: "bg-indigo-500/20 text-indigo-400 border-indigo-500/30",
      pink: "bg-pink-500/20 text-pink-400 border-pink-500/30",
      cyan: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30"
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  const usePrompt = (prompt: string) => {
    // Store the prompt in localStorage to be picked up by the main chat
    localStorage.setItem("veritus-selected-prompt", prompt);
    setLocation("/");
  };

  const copyPrompt = (prompt: string) => {
    navigator.clipboard.writeText(prompt);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/80 backdrop-blur-md">
        <div className="container flex h-16 items-center justify-between px-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setLocation("/")}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                <span className="text-white font-bold text-sm">T</span>
              </div>
              <span className="font-semibold text-lg">AcronIQ Veritus</span>
            </button>
            <div className="h-6 w-px bg-border mx-2" />
            <h1 className="text-xl font-semibold flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Smart Prompts
            </h1>
          </div>

          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLocation("/signals")}
              className="text-muted-foreground hover:text-foreground"
            >
              Signals
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLocation("/reports")}
              className="text-muted-foreground hover:text-foreground"
            >
              Reports
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLocation("/")}
              className="text-muted-foreground hover:text-foreground"
            >
              Back to Chat
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-8 py-8">
        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search prompts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <div className="flex gap-2 flex-wrap">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                className="capitalize"
              >
                {category === "all" ? "All Categories" : category}
              </Button>
            ))}
          </div>
        </div>

        {/* Prompts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPrompts.map((prompt) => {
            const IconComponent = prompt.icon;
            return (
              <Card key={prompt.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getColorClasses(prompt.color)}`}>
                        <IconComponent className="h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{prompt.title}</CardTitle>
                        <Badge variant="secondary" className="text-xs mt-1">
                          {prompt.category}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground line-clamp-3">
                    {prompt.prompt}
                  </p>
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => usePrompt(prompt.prompt)}
                      className="flex-1"
                      size="sm"
                    >
                      <ArrowRight className="h-4 w-4 mr-2" />
                      Use Prompt
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyPrompt(prompt.prompt)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredPrompts.length === 0 && (
          <div className="text-center py-12">
            <Zap className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No prompts found</h3>
            <p className="text-muted-foreground">
              Try adjusting your search terms or category filter
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
